'use client'

/**
 * To be used in wrapper (Client side) to use
 * normal hooks to fetch data (useSuspenseQuery, useMutation, etc)
 */
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
} from '@apollo/experimental-nextjs-app-support'
import posthog from 'posthog-js'
import { v4 as uuidv4 } from 'uuid'

import {
  EXPIRED_PARAM_TOKEN_VALUE,
  HOME_ERROR_PARAM,
  UNAUTHORIZED_SESSION_ERROR,
} from '@/modules/auth/constants'
import {
  ApolloLink,
  FetchResult,
  from,
  HttpLink,
  Observable,
  RequestHandler,
  useApolloClient,
} from '@apollo/client'
import { MainRoutes } from '@/utils/routes'
import { X_FORWARDED_FOR_HEADER } from '@/modules/app/constants'

const TIMEOUT_DURATION = 30000 // 30 seconds

// https://www.apollographql.com/docs/react/api/link/apollo-link-retry#options
// const retryLink = new RetryLink({
//   delay: {
//     initial: 300, // default
//     max: Infinity, // default
//     jitter: false,
//   },
//   attempts: {
//     max: 2, // default
//     retryIf: (error, _operation) => Boolean(error),
//   },
// })

const httpLink = new HttpLink({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_API_URL,
})

const timeoutRequestHandler: RequestHandler = (operation, forward) => {
  const timeoutObservable = () =>
    new Observable<FetchResult>((observer) => {
      const timeoutId = setTimeout(() => {
        observer.error({
          networkError: new Error(
            `GraphQL request timed out after ${TIMEOUT_DURATION}ms`,
          ),
          operation,
        })
        posthog.captureException('GraphQL client wrapper request timed out', {
          operationName: operation.operationName,
          operationVariables: operation.variables,
          operationQuery: operation.query,
        })
        timeoutObservable()
      }, TIMEOUT_DURATION)

      return forward(operation).subscribe({
        next: (result) => {
          clearTimeout(timeoutId)
          observer.next(result)
          observer.complete()
        },
        complete: () => {
          clearTimeout(timeoutId)
          observer.complete()
        },
      })
    })
  return timeoutObservable()
}
const timeoutLink = new ApolloLink(timeoutRequestHandler)

function makeClient({
  authToken,
  signOut,
  ipAddress,
}: {
  authToken: string | undefined | null
  signOut: () => Promise<void>
  ipAddress: string | null
}) {
  const authLink = setContext(async (graphqlRequest, { headers }) => {
    return {
      headers: {
        ...headers,
        Authorization: authToken ? `Bearer ${authToken}` : '',
        'x-trace-id': uuidv4().substring(0, 8),
        [X_FORWARDED_FOR_HEADER]: ipAddress,
        'operation-name': graphqlRequest.operationName,
      },
    }
  })

  const responseInterceptorLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      if (!response || Object.keys(response).length === 0) {
        posthog.captureException(
          'No response body received (Possible Timeout)',
          {
            operationName: operation.operationName,
            operationVariables: operation.variables,
            operationQuery: operation.query,
          },
        )
        throw new Error('No response body received')
      }
      return response
    })
  })

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        const { message, locations, path } = error
        if (
          message.toLowerCase?.()?.includes('not authenticated') ||
          (error as unknown as { code: string })?.code === 'INVALID_TOKEN'
        ) {
          console.warn('[GQL ERRORS]', UNAUTHORIZED_SESSION_ERROR)
          signOut()
          return
        }

        console.warn(
          `[GraphQL client wrapper error]: Message: ${message}, 
            Location: ${JSON.stringify(locations)}, Path: ${path},
           Operation: ${JSON.stringify(operation)}`,
        )
      })
      posthog.captureException(
        'GraphQL client wrapper error',
        graphQLErrors?.reduce(
          (acc, error, index) => ({ ...acc, [index]: error.toString() }),
          {},
        ),
      )
    }

    console.log('networkError', networkError)

    if (networkError) {
      console.warn(`[Network error]: ${networkError}`)
      if (
        (networkError as unknown as Record<string, unknown>).statusCode === 401
      ) {
        console.warn('[NETWORK ERRORS]', UNAUTHORIZED_SESSION_ERROR)
        signOut()
        return
      }

      posthog.captureException('GraphQL client wrapper network error', {
        errorMessage: networkError?.message,
        errorStack: networkError?.stack,
        errorName: networkError?.name,
        errorCause: networkError?.cause,
      })
    }
  })

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        SalaryType: {
          keyFields: ['salaryId'],
        },
      },
    }),
    link: from([
      authLink,
      responseInterceptorLink,
      // Disable retryLink for now for AuthJS (intended for Clerk and no-body responses that are
      // now handled by responseInterceptorLink)
      // NOTE: it is important that the retryLink comes before the
      // timeoutLink. When timeoutLink calls observer.error(),
      // it'll return back through the link chain
      // and call the retryLink.
      //retryLink,
      timeoutLink,
      errorLink,
      httpLink,
    ]),
  })
}

// https://github.com/apollographql/apollo-client-nextjs/issues/103
function UpdateAuth({
  children,
  isAuthenticated,
}: React.PropsWithChildren<{
  isAuthenticated: boolean
}>) {
  const apolloClient = useApolloClient()
  // just synchronously update the `apolloClient.defaultContext` before any child component can be rendered
  // so the value is available for any query started in a child
  apolloClient.defaultContext.isAuthenticated = isAuthenticated
  return <>{children}</>
}

export function ApolloWrapper({
  children,
  token,
  isAuthenticated,
  ipAddress,
}: React.PropsWithChildren<{
  token: string | undefined | null
  isAuthenticated: boolean
  ipAddress: string | null
}>) {
  /**
   * Sign out the user and redirect to the home page with an error message
   * since is client side we should always have window.location available
   */
  const signOut = async () => {
    if (typeof window !== 'undefined') {
      window.location.href = `${MainRoutes.Logout}?${HOME_ERROR_PARAM}=${EXPIRED_PARAM_TOKEN_VALUE}`
    }
  }

  return (
    <ApolloNextAppProvider
      makeClient={() =>
        makeClient({
          authToken: token,
          // eslint-disable-next-line max-len
          // https://marithealth.sentry.io/issues/6279350419/?alert_rule_id=15476553&alert_type=issue&notification_uuid=33147795-9869-47d0-a6d0-e4dccef3b010&project=4508027419164672&referrer=slack
          signOut,
          ipAddress,
        })
      }
    >
      <UpdateAuth isAuthenticated={isAuthenticated}>{children}</UpdateAuth>
    </ApolloNextAppProvider>
  )
}
